import { useEffect, useRef, useState } from "react";
import { LuFilter, LuX, LuSearch, LuPlus } from "react-icons/lu";
import Modal from "../../../common/Modal";
import Button from "../../../common/Buttons";
import TextInput from "../../../common/TextInput";
import SearchableSelectInput from "../../../common/SearchableSelectInput";
import InventoryFormModal from "./InventoryFormModal";

const EMPTY_FORM = {
  item: "",
  type: "",
  catalog: "",
  manufacturer: "",
  supplier: "",
  project: "",
  location: "",
  quantity: ["", ""],
  addDate: ["", ""],
  expireDate: ["", ""],
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const toISODate = (d) => d.toISOString().slice(0, 10);

const EXPIRY_PRESETS = [
  { value: "", label: "Any expiry" },
  { value: "expiringSoon", label: "Expiring within 30 days" },
  { value: "expired", label: "Already expired" },
];

const SectionLabel = ({ children }) => (
  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{children}</p>
);

export default function InventoryFilter({
  query,
  onApply,
  onClear,
  itemNames = [],
  types = [],
  manufacturers = [],
  suppliers = [],
  projects = [],
  locations = [],
  disabled,
  handleAdd = () => {},
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [expiryPreset, setExpiryPreset] = useState("");

  const [addFormOpen, setAddFormOpen] = useState(false);
  const openAddModal = () => setAddFormOpen(true);
  // const handleAddSubmit = (values) => {
  //   handleAdd(values);
  //   setAddFormOpen(false);
  // };


  const handleAddSubmit = (values) => {
  if (typeof handleAdd === "function") {
    handleAdd(values);
  }
  setAddFormOpen(false);
};
  const [searchTerm, setSearchTerm] = useState(query?.item || "");
  const [searchPending, setSearchPending] = useState(false);
  const searchDebounce = useRef(null);

  useEffect(() => {
    setSearchTerm(query?.item || "");
  }, [query?.item]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSearchPending(true);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      onApply({ ...query, item: value.trim() });
      setSearchPending(false);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, []);


  useEffect(() => {
    if (open) {
      setForm(
        query && Object.keys(query).length > 0
          ? {
              ...EMPTY_FORM,
              ...query,
              quantity: query.quantity || ["", ""],
              addDate: query.addDate || ["", ""],
              expireDate: query.expireDate || ["", ""],
            }
          : EMPTY_FORM,
      );
      setExpiryPreset("");
    }
  }, [open, query]);

  const setField = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  const setRange = (field, idx) => (e) =>
    setForm((f) => {
      const next = [...f[field]];
      next[idx] = e.target.value;
      return { ...f, [field]: next };
    });

  const handleExpiryPreset = (value) => {
    setExpiryPreset(value);
    if (value === "expiringSoon") {
      setForm((f) => ({ ...f, expireDate: [toISODate(startOfToday()), toISODate(addDays(startOfToday(), 30))] }));
    } else if (value === "expired") {
      setForm((f) => ({ ...f, expireDate: ["", toISODate(startOfToday())] }));
    } else {
      setForm((f) => ({ ...f, expireDate: ["", ""] }));
    }
  };

  const hasActiveQuery = query && Object.keys(query).length > 0;

  const handleApply = () => {
    onApply({
      item: form.item.trim(),
      type: form.type,
      catalog: form.catalog.trim(),
      manufacturer: form.manufacturer,
      supplier: form.supplier,
      project: form.project,
      location: form.location,
      quantity: form.quantity,
      addDate: form.addDate,
      expireDate: form.expireDate,
    });
    setSearchTerm(form.item.trim());
    setOpen(false);
  };

  const handleClearAll = () => {
    setSearchTerm("");
    setSearchPending(false);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    setExpiryPreset("");
    onClear();
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <div className="relative flex-1 sm:max-w-xs">
          {searchPending ? (
            <span className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 animate-spin rounded-full border-2 border-slate-300 border-t-brand-500" />
          ) : (
            <LuSearch size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          )}
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={disabled}
            placeholder="Search items…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-3 text-[13px] outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 text-[13px] font-medium text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <LuFilter size={14} />
          <span className="hidden sm:inline">Filters Inventory</span>
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={openAddModal}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-brand-gradient px-3 text-[13px] font-semibold text-white shadow-brand transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <LuPlus size={14} />
          <span className="hidden sm:inline">Add Inventory</span>
        </button>

        {hasActiveQuery && (
          <button
            type="button"
            onClick={handleClearAll}
            title="Clear filters"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <LuX size={14} />
          </button>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Filter Inventory">
        <div className="max-h-[65vh] space-y-5 overflow-y-auto pr-1 sm:max-h-[70vh]">
          {/* Identity */}
          <div>
            <SectionLabel>Identity</SectionLabel>
            <div className="grid gap-4 sm:grid-cols-2">
              <SearchableSelectInput
                name="item"
                label="Item Name"
                options={itemNames.map((n) => ({ value: n, label: n }))}
                placeholder="Any item"
                searchPlaceholder="Search item..."
                value={form.item}
                onChange={(e) => setField("item")(e.target.value)}
              />
              <TextInput
                label="Catalog"
                value={form.catalog}
                onChange={(e) => setField("catalog")(e.target.value)}
                placeholder="Batch / catalog #"
              />
            </div>
          </div>

          {/* Classification */}
          <div>
            <SectionLabel>Classification</SectionLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SearchableSelectInput
                name="type"
                label="Type"
                options={types.map((t) => ({ value: t, label: t }))}
                placeholder="Any type"
                searchPlaceholder="Search type..."
                value={form.type}
                onChange={(e) => setField("type")(e.target.value)}
              />
              <SearchableSelectInput
                name="manufacturer"
                label="Manufacturer"
                options={manufacturers.map((m) => ({ value: m, label: m }))}
                placeholder="Any manufacturer"
                searchPlaceholder="Search manufacturer..."
                value={form.manufacturer}
                onChange={(e) => setField("manufacturer")(e.target.value)}
              />
              <SearchableSelectInput
                name="supplier"
                label="Supplier"
                options={suppliers.map((s) => ({ value: s, label: s }))}
                placeholder="Any supplier"
                searchPlaceholder="Search supplier..."
                value={form.supplier}
                onChange={(e) => setField("supplier")(e.target.value)}
              />
            </div>
          </div>

          {/* Logistics */}
          <div>
            <SectionLabel>Logistics</SectionLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SearchableSelectInput
                name="location"
                label="Location"
                options={locations.map((l) => ({ value: l, label: l }))}
                placeholder="Any location"
                searchPlaceholder="Search location..."
                value={form.location}
                onChange={(e) => setField("location")(e.target.value)}
              />
              <SearchableSelectInput
                name="project"
                label="Project"
                options={projects.map((p) => ({ value: p, label: p }))}
                placeholder="Any project"
                searchPlaceholder="Search project..."
                value={form.project}
                onChange={(e) => setField("project")(e.target.value)}
              />
              <div>
                <label className="mb-1 block text-[12px] font-medium text-slate-600">Quantity</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    placeholder="Min"
                    value={form.quantity[0]}
                    onChange={setRange("quantity", 0)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  />
                  <span className="text-slate-400">–</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="Max"
                    value={form.quantity[1]}
                    onChange={setRange("quantity", 1)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dates & expiry */}
          <div>
            <SectionLabel>Dates &amp; Expiry</SectionLabel>
            <div className="grid gap-4">
              <div>
                <label className="mb-1 block text-[12px] font-medium text-slate-600">Expiry Status</label>
                <select
                  value={expiryPreset}
                  onChange={(e) => handleExpiryPreset(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                >
                  {EXPIRY_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="mb-1 block text-[12px] font-medium text-slate-600">Added Between</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={form.addDate[0] || ""}
                      onChange={setRange("addDate", 0)}
                      className="w-full min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    />
                    <span className="shrink-0 text-slate-400">–</span>
                    <input
                      type="date"
                      value={form.addDate[1] || ""}
                      onChange={setRange("addDate", 1)}
                      className="w-full min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[12px] font-medium text-slate-600">
                    Expires Between
                    {expiryPreset && <span className="ml-1.5 font-normal text-slate-400">(from preset — editable)</span>}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={form.expireDate[0] || ""}
                      onChange={(e) => {
                        setExpiryPreset("");
                        setRange("expireDate", 0)(e);
                      }}
                      className="w-full min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    />
                    <span className="shrink-0 text-slate-400">–</span>
                    <input
                      type="date"
                      value={form.expireDate[1] || ""}
                      onChange={(e) => {
                        setExpiryPreset("");
                        setRange("expireDate", 1)(e);
                      }}
                      className="w-full min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="solid" onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </Modal>

      <InventoryFormModal
        open={addFormOpen}
        mode="add"
        initialValue={null}
        onClose={() => setAddFormOpen(false)}
        onSubmit={handleAddSubmit}
      />
    </>
  );
}